import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useSupabaseAuth } from '../providers/SupabaseAuthProvider';
import '../components/auth/AuthModal.css';

/**
 * ResetPasswordPage - Handles password reset/update after user clicks email link
 * User arrives here via password reset email link
 * After successful password update, user is auto-logged in and redirected
 */
function ResetPasswordPage() {
  const navigate = useNavigate();
  const { session, user } = useSupabaseAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidRecovery, setIsValidRecovery] = useState(null); // null = checking, true = valid, false = invalid

  // Check if this is a valid password recovery session
  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        // Get current session
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error checking session:', error);
          setIsValidRecovery(false);
          return;
        }

        // Check if user is logged in (recovery links auto-log users in)
        if (currentSession && currentSession.user) {
          console.log('[ResetPassword] Valid recovery session detected');
          setIsValidRecovery(true);
        } else {
          console.log('[ResetPassword] No active session - invalid or expired link');
          setIsValidRecovery(false);
        }
      } catch (err) {
        console.error('Error checking recovery session:', err);
        setIsValidRecovery(false);
      }
    };

    checkRecoverySession();
  }, []);

  // Calculate password strength
  const calculatePasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength += 20;
    if (pwd.length >= 12) strength += 20;
    if (/[a-z]/.test(pwd)) strength += 20;
    if (/[A-Z]/.test(pwd)) strength += 20;
    if (/[0-9]/.test(pwd)) strength += 20;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 20;
    return Math.min(strength, 100);
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  // Handle password update
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordStrength < 40) {
      setError('Please choose a stronger password');
      return;
    }

    setLoading(true);

    try {
      // Update user's password
      const { data, error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      console.log('[ResetPassword] Password updated successfully');
      setSuccess(true);

      // Wait 2 seconds then redirect to services page (user is already logged in)
      setTimeout(() => {
        navigate('/community/services');
      }, 2000);

    } catch (err) {
      console.error('Password update error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
      setLoading(false);
    }
  };

  // Show loading state while checking recovery session
  if (isValidRecovery === null) {
    return (
      <div className="modal-overlay" style={{ position: 'fixed', zIndex: 9999 }}>
        <div className="modal-content" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2>Password Reset</h2>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if invalid/expired recovery link
  if (isValidRecovery === false) {
    return (
      <div className="modal-overlay" style={{ position: 'fixed', zIndex: 9999 }}>
        <div className="modal-content" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2>Invalid Reset Link</h2>
          </div>
          <div className="modal-body" style={{ padding: '30px 20px' }}>
            <div style={{
              padding: '20px',
              background: '#ffebee',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#c62828', fontSize: '1.1rem', marginBottom: '10px' }}>
                ❌ This password reset link is invalid or has expired.
              </p>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Password reset links expire after 24 hours.
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button
                className="btn-primary"
                onClick={() => navigate('/community/services')}
                style={{ marginBottom: '10px', width: '100%' }}
              >
                Go to Services & Request New Link
              </button>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '15px' }}>
                You can request a new password reset link from the login page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="modal-overlay" style={{ position: 'fixed', zIndex: 9999 }}>
        <div className="modal-content" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2>Password Updated!</h2>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              padding: '30px',
              background: '#e8f5e9',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '15px' }}>✅</div>
              <h3 style={{ color: '#2e7d32', marginBottom: '10px' }}>
                Password Successfully Updated!
              </h3>
              <p style={{ color: '#666', fontSize: '0.95rem' }}>
                You're now logged in and will be redirected shortly...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show password update form
  return (
    <div className="modal-overlay" style={{ position: 'fixed', zIndex: 9999 }}>
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>Set New Password</h2>
          <button
            className="modal-close"
            onClick={() => navigate('/community/services')}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleUpdatePassword}>
            <div style={{
              padding: '15px',
              background: '#e3f2fd',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              color: '#1565c0'
            }}>
              ℹ️ Create a new password for your account: <strong>{user?.email}</strong>
            </div>

            {/* New Password Field */}
            <div className="form-group">
              <label>New Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="form-input"
                  autoFocus
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    opacity: 0.6
                  }}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{
                    height: '6px',
                    background: '#e0e0e0',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${passwordStrength}%`,
                      background: passwordStrength < 40 ? '#f44336' :
                                 passwordStrength < 70 ? '#ff9800' : '#4caf50',
                      transition: 'all 0.3s ease'
                    }} />
                  </div>
                  <p style={{
                    fontSize: '0.85rem',
                    marginTop: '5px',
                    color: passwordStrength < 40 ? '#f44336' :
                           passwordStrength < 70 ? '#ff9800' : '#4caf50'
                  }}>
                    Strength: {passwordStrength < 40 ? 'Weak' :
                               passwordStrength < 70 ? 'Good' : 'Strong'}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="form-group">
              <label>Confirm New Password *</label>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="form-input"
                minLength={8}
              />
              {confirmPassword && password !== confirmPassword && (
                <p style={{ color: '#f44336', fontSize: '0.85rem', marginTop: '5px' }}>
                  ⚠️ Passwords do not match
                </p>
              )}
            </div>

            {/* Password Requirements */}
            <div style={{
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '6px',
              fontSize: '0.85rem',
              marginBottom: '20px'
            }}>
              <p style={{ fontWeight: '600', marginBottom: '8px' }}>Password must:</p>
              <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                <li style={{ color: password.length >= 8 ? '#4caf50' : '#666' }}>
                  Be at least 8 characters long
                </li>
                <li style={{ color: /[A-Z]/.test(password) ? '#4caf50' : '#666' }}>
                  Include uppercase letters
                </li>
                <li style={{ color: /[a-z]/.test(password) ? '#4caf50' : '#666' }}>
                  Include lowercase letters
                </li>
                <li style={{ color: /[0-9]/.test(password) ? '#4caf50' : '#666' }}>
                  Include numbers
                </li>
              </ul>
            </div>

            {error && (
              <div className="error-message" style={{ marginBottom: '20px' }}>
                {error}
              </div>
            )}

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/community/services')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !password || password !== confirmPassword || passwordStrength < 40}
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
